/**
 * The profile fields the SERVER writes, read from the database it writes them
 * to ([[BUGS #190]]).
 *
 * Same argument as `entitlementService`, applied one layer over. The app reads
 * `users/{uid}` through the Firebase web SDK, which is pinned to the
 * PRODUCTION project because Auth is deliberately shared — while
 * `update-profile` goes through the gateway and writes the project the backend
 * runs in. On staging those are different databases, so a preference saved
 * successfully came back wrong on the next load.
 *
 * **Four user-visible bugs had this one shape** before it was named: saved
 * portfolios reading empty ([[BUGS #190]]), report sharing 404ing, the
 * onboarding and quiz dismissals never sticking ([[BUGS #185]]), and dark mode
 * reverting on every page load ([[BUGS #203]]). Each was diagnosed separately.
 * This closes the class for the fields the gateway owns.
 *
 * **Deliberately narrow.** Only the fields whose writer IS the gateway. The
 * snapshot still answers everything else, and it must stay the answer for
 * those — a second source for a fact the client already owns correctly would
 * be a new version of the same problem.
 */
import { getAuth } from 'firebase/auth';

import { gatewayFetch } from './gateway';

/** Exactly the keys `user_api.SERVER_OWNED_PROFILE_FIELDS` returns. */
export interface ServerProfile {
  preferences?: Record<string, unknown>;
  onboardingDismissed?: boolean;
  quizPromptDismissed?: boolean;
  onboardingTasks?: Record<string, unknown>;
  onboardingChecklist?: Record<string, unknown>;
  signalPreferences?: Record<string, boolean>;
  newsletterOptIn?: unknown;
}

/**
 * Fetch the server-owned fields, or `null` when we could not find out.
 *
 * **`null` is not `{}`** and the distinction is the whole point: an empty
 * object would mean "the server says you have no preferences", which a caller
 * would rightly apply — reverting a theme or un-dismissing a panel because a
 * read hiccuped is precisely the defect this exists to close. `null` means
 * *leave what you have alone*.
 */
export async function fetchServerProfile(): Promise<ServerProfile | null> {
  const user = getAuth().currentUser;
  if (!user) return null;
  try {
    const res = await gatewayFetch('user', 'profile', { auth: { token: await user.getIdToken() } });
    if (!res.ok) return null;
    const body = await res.json();
    // `degraded` means the server could not read it either — same treatment as
    // a failed request, for the same reason.
    if (!body?.success || body?.degraded) return null;
    const profile = (body.profile ?? {}) as ServerProfile;
    rememberServerProfile(profile);
    return profile;
  } catch {
    return null;
  }
}


/**
 * The server-owned fields as last known, shared by the reader and the writer.
 *
 * Module-level rather than context state, because two places need the same
 * value and neither should re-render the app to share it: `AuthContext` layers
 * it over each Firestore snapshot, and `useProfileUpdate` patches it the
 * moment a write succeeds.
 *
 * **That second half is not optional.** Without it the cache holds the value
 * from sign-in, so the next snapshot would layer the OLD server value back
 * over the change just made — the same revert this whole fix exists to stop,
 * one step further along.
 */
let cached: ServerProfile = {};

export function getServerProfile(): ServerProfile {
  return cached;
}

/** Record fields the client just wrote, so a later snapshot does not undo them. */
export function rememberServerProfile(patch: ServerProfile): void {
  cached = { ...cached, ...patch };
}

export function clearServerProfile(): void {
  cached = {};
}
